import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Platform,
  Pressable,
  useWindowDimensions,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { CategoryChips } from '@/components/discovery/category-chips';
import { CitySelectorSheet } from '@/components/discovery/city-selector-sheet';
import { ProviderCard } from '@/components/discovery/provider-card';
import { SearchFiltersSheet } from '@/components/discovery/search-filters-sheet';
import { SeoHead } from '@/components/seo-head';
import { pageTitle } from '@/lib/seo/meta';
import { ThemedText } from '@/components/themed-text';
import { SearchInput } from '@/components/ui/search-input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  trackDiscoveryEvent,
  useDiscoveryCategories,
  useSearchProviders,
} from '@/hooks/use-discovery';
import { useRegionStore } from '@/store/regionStore';
import { DiscoveryFilters, ProviderCard as ProviderCardType } from '@/types/discovery';

const SEARCH_DEBOUNCE_MS = 350;

/**
 * Busca pública de prestadores (B2). Fora de `(tabs)`: roda deslogada, na web e
 * no app — quem achar aqui agenda como convidado, sem criar conta.
 */
export default function SearchScreen() {
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  const { region, setRegion } = useRegionStore();

  // No mobile web o autofoco abre o teclado assim que a tela monta e joga o
  // layout para cima; só o desktop entra com o campo focado.
  const autoFocusSearch = Platform.OS === 'web' && width >= 820;

  const [term, setTerm] = useState('');
  const [debouncedTerm, setDebouncedTerm] = useState('');
  const [categories, setCategories] = useState<string[]>([]);
  const [filters, setFilters] = useState<DiscoveryFilters>({});
  const [cityPickerOpen, setCityPickerOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedTerm(term), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [term]);

  const activeFilters = useMemo<DiscoveryFilters>(
    () => ({ ...filters, q: debouncedTerm, categories }),
    [filters, debouncedTerm, categories],
  );

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useSearchProviders(
    region,
    activeFilters,
  );
  const { data: categoryCatalog } = useDiscoveryCategories();

  const items = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data]);
  const total = data?.pages[0]?.total ?? 0;
  const regionLabel = region.city ? `${region.city} · ${region.uf}` : 'Todo o Brasil';

  const trackedRef = useRef<string>('');

  useEffect(() => {
    if (items.length === 0) return;

    const signature = items.map((item) => item.providerId).join(',');
    if (trackedRef.current === signature) return;
    trackedRef.current = signature;

    items.forEach((card, index) => {
      trackDiscoveryEvent({
        providerId: card.providerId,
        type: 'IMPRESSION',
        surface: 'SEARCH_RESULTS',
        sponsorshipId: card.sponsorshipId,
        query: debouncedTerm,
        region,
        position: index + 1,
      });
    });
  }, [items, debouncedTerm, region]);

  // A navegação é do `<Link>` do card; aqui fica só a métrica do clique.
  const trackProviderClick = (card: ProviderCardType, index: number) => {
    trackDiscoveryEvent({
      providerId: card.providerId,
      type: 'CLICK',
      surface: 'SEARCH_RESULTS',
      sponsorshipId: card.sponsorshipId,
      query: debouncedTerm,
      region,
      position: index + 1,
    });
  };

  const toggleCategory = (slug: string) => {
    setCategories((current) =>
      current.includes(slug) ? current.filter((item) => item !== slug) : [...current, slug],
    );
  };

  const hasExtraFilters =
    filters.bookableOnly ||
    filters.hasGallery ||
    filters.minPrice !== undefined ||
    filters.maxPrice !== undefined ||
    (filters.sort && filters.sort !== 'relevance');

  return (
    <View className="flex-1 bg-canvas" style={{ paddingTop: insets.top }}>
      <SeoHead
        title={pageTitle('Buscar profissionais')}
        description="Encontre profissionais e negócios de serviço que atendem pela Controlapag: busque por categoria e cidade e agende horário direto pela página pública."
        path="/buscar"
      />
      <View className="gap-3 bg-card pb-3 pt-2">
        {/* Título e introdução no HTML estático: é o que o buscador lê da página. */}
        <View className="gap-0.5 px-5 pt-1">
          <ThemedText role="heading" aria-level={1} className="font-bold text-xl text-foreground">
            Buscar profissionais
          </ThemedText>
          <ThemedText className="text-sm text-neutral-500">
            Profissionais com página pública na Controlapag. Busque por serviço, nome ou cidade e
            agende direto pela página de cada um.
          </ThemedText>
        </View>
        <View className="flex-row items-center gap-3 px-5">
          <Pressable onPress={() => router.back()} hitSlop={10}>
            <Ionicons className="text-neutral-900" name="chevron-back" size={22} />
          </Pressable>

          <View className="flex-1">
            <SearchInput
              value={term}
              onChangeText={setTerm}
              placeholder="Busque por serviço, nome ou categoria"
              autoFocus={autoFocusSearch}
            />
          </View>

          <Pressable onPress={() => setFiltersOpen(true)} hitSlop={10}>
            <View className="relative">
              <Ionicons className="text-neutral-900" name="options-outline" size={22} />
              {hasExtraFilters ? (
                <View className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-primary" />
              ) : null}
            </View>
          </Pressable>
        </View>

        <Pressable
          onPress={() => setCityPickerOpen(true)}
          className="flex-row items-center gap-1 px-5"
          hitSlop={8}
        >
          <Ionicons name="location" size={13} color="#FD5001" />
          <ThemedText className="text-xs font-semibold text-primary">{regionLabel}</ThemedText>
          <Ionicons name="chevron-down" size={13} color="#FD5001" />
        </Pressable>

        <CategoryChips
          categories={categoryCatalog ?? []}
          selected={categories}
          onToggle={toggleCategory}
        />
      </View>

      <FlatList
        data={items}
        keyExtractor={(item) => item.providerId}
        numColumns={2}
        columnWrapperStyle={{ gap: 12 }}
        contentContainerStyle={{ padding: 20, gap: 12, paddingBottom: insets.bottom + 40 }}
        onEndReached={() => {
          if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
        }}
        onEndReachedThreshold={0.4}
        keyboardShouldPersistTaps="handled"
        ListHeaderComponent={
          !isLoading && items.length > 0 ? (
            <ThemedText className="pb-1 text-xs text-neutral-500">
              {total} {total === 1 ? 'profissional encontrado' : 'profissionais encontrados'}
            </ThemedText>
          ) : null
        }
        renderItem={({ item, index }) => (
          <View className="flex-1">
            <ProviderCard
              card={item}
              href={`/@${item.slug}`}
              onPress={() => trackProviderClick(item, index)}
            />
          </View>
        )}
        ListEmptyComponent={
          isLoading ? (
            <View className="flex-row flex-wrap gap-3">
              {[0, 1, 2, 3].map((key) => (
                <Skeleton key={key} className="h-56 flex-1 rounded-2xl" style={{ minWidth: 140 }} />
              ))}
            </View>
          ) : (
            <EmptyResults
              term={debouncedTerm}
              regionLabel={regionLabel}
              hasRegion={Boolean(region.uf)}
              onClearRegion={() => setRegion({ uf: null, city: null })}
            />
          )
        }
        ListFooterComponent={
          isFetchingNextPage ? (
            <View className="items-center py-6">
              <ActivityIndicator color="#FD5001" />
            </View>
          ) : null
        }
      />

      <CitySelectorSheet
        open={cityPickerOpen}
        region={region}
        onClose={() => setCityPickerOpen(false)}
        onSelect={setRegion}
      />

      <SearchFiltersSheet
        open={filtersOpen}
        filters={filters}
        onClose={() => setFiltersOpen(false)}
        onApply={setFilters}
      />
    </View>
  );
}

/** Vazio com saída: ampliar a busca para o Brasil em vez de tela morta (Cenário 7). */
function EmptyResults({
  term,
  regionLabel,
  hasRegion,
  onClearRegion,
}: {
  term: string;
  regionLabel: string;
  hasRegion: boolean;
  onClearRegion: () => void;
}) {
  return (
    <View className="items-center gap-3 px-6 py-16">
      <View className="h-14 w-14 items-center justify-center rounded-2xl bg-neutral-100">
        <Ionicons className="text-neutral-400" name="search-outline" size={24} />
      </View>

      <ThemedText className="text-center text-base font-semibold text-foreground">
        {term ? `Nada encontrado para "${term}"` : 'Nenhum profissional por aqui ainda'}
      </ThemedText>

      <ThemedText className="text-center text-sm leading-5 text-neutral-500">
        {hasRegion
          ? `Não achamos profissionais em ${regionLabel}.`
          : 'Tente outro termo ou remova os filtros.'}
      </ThemedText>

      {hasRegion ? (
        <Pressable
          onPress={onClearRegion}
          className="mt-1 rounded-full bg-primary px-4 py-2.5"
          style={{ borderCurve: 'continuous' }}
        >
          <ThemedText className="text-sm font-semibold text-white">
            Buscar em todo o Brasil
          </ThemedText>
        </Pressable>
      ) : null}
    </View>
  );
}
